<template>
  <ConsultationBanner v-if="isBannerVisible" bg-color="fg-disabled">
    <template #consultationPill>
      <ScheduledConsultationChip class="w-fit sm:w-auto" />
    </template>

    <template #mainText>
      <p class="font-semibold line-clamp-3" data-testid="main-text">
        {{ renderMainText() }}
      </p>
    </template>

    <template #subText>
      <p data-testid="sub-text">
        <span class="font-semibold">{{ `${t('Invitation from')}: ` }}</span>
        <span>{{ getPatientData() }}</span>
      </p>
    </template>

    <template #actions>
      <VBtn
        data-testid="consultations-overview-button"
        variant="text"
        class="button-large pa-0"
        :class="`text-white`"
        :to="{ name: routeNames.CONSULTATIONS }"
        :active="false"
      >
        {{ t('Open consultation overview') }}
      </VBtn>

      <VBtn
        v-for="action in consultationActions"
        :key="action.actionType"
        :variant="setButtonVariant(action.actionType)"
        :text="action.actionName"
        :color="'gray-700'"
        class="text-sm font-semibold bg-white"
        @click="action.actionCallback"
      />

      <VBtn
        icon="close"
        variant="plain"
        size="x-small"
        :ripple="false"
        data-testid="btn-close"
        @click="isBannerVisible = false"
      />
    </template>
  </ConsultationBanner>
</template>

<script setup lang="ts">
import ConsultationBanner from '@/components/consultationrequest/ConsultationBanner.vue'
import { translateScheduledConsultationTypeDTO } from '@/util/translations'
import { type ScheduledConsultationDTO, ScheduledConsultationTypeDTO } from '@/generated'
import { useI18n } from 'vue-i18n'
import useScheduledConsultationActions from '@/composables/useScheduledConsultationActions'
import { routeNames } from '@/router/routeNames'
import { ActionType } from '@/enums/ActionType'
import { ref } from 'vue'
import ScheduledConsultationChip from '@/components/consultationrequest/ScheduledConsultationChip.vue'
import { useFormatDate } from '@/composables/useFormatDate'

interface Props {
  consultation: ScheduledConsultationDTO
}

const { consultation } = defineProps<Props>()

const { t, locale } = useI18n()
const { consultationActions } = useScheduledConsultationActions(consultation)
const { formatDateTime } = useFormatDate()

const isBannerVisible = ref(true)

function setButtonVariant(actionType: ActionType) {
  if (actionType === ActionType.DECLINE) {
    return 'text'
  }

  return 'flat'
}

function renderMainText(): string {
  const isGerman = locale.value === 'de'

  const consultationTitle =
    consultation.type === ScheduledConsultationTypeDTO.CONFERENCE
      ? t('Conference {title}', { title: consultation.conferenceTopic })
      : translateScheduledConsultationTypeDTO(consultation.type, t)

  return (
    consultationTitle +
    ` ${t('scheduled for')} ${formatDateTime(consultation.scheduledStartTime)} ${isGerman ? 'Uhr' : ''}`
  )
}

function getPatientData(): string {
  const { user, wards, departments, hospitals } = consultation.requestedBy
  const firstName = user.firstName
  const lastName = user.lastName
  const wardName = wards[0]?.name
  const departmentName = departments[0]?.name
  const hospitalName = hospitals[0]?.name

  const fullName = [firstName, lastName].filter(Boolean).join(' ')
  const firstPart = [fullName, wardName].filter(Boolean).join(' · ')
  return [firstPart, departmentName, hospitalName].filter(Boolean).join(', ')
}
</script>

<style scoped></style>
